import { luceneEscape } from '../escape-utils'
import {
  CustomerDataParsed,
  CustomerMapParsed,
  MatchboiError,
} from '../interfaces'

import { Tool } from './base'
import {
  escapeRx,
  etaFieldLookup,
  InlineGroups,
  makeRenderer,
  normalizeList,
  RenderContext,
  scalarToString,
} from './render/render-kit'
import { DatasourceMap } from './render/rtd/datasource.types'
import { AstPredicate, ValueOrList } from './render/types'
//import rison from 'rison-node';

// Elastic has no datasource prelude (renderTuning is a bare Lucene-like body),
// so generic fields like process_path bleed into registry_event / dns / etc.
// queries when the customer's matrix carries them. unsupportedFields here
// blacklists the leak fields per non-core category — applied by
// renderSiemGrouped after matrix pruning — so the query for each category
// only carries fields that semantically belong to it.
const CORE_PROCESS_LEAK = [
  'process_path',
  'process_name',
  'process_cmdline',
  'target_path',
  'md5',
  'sha256',
]
const CORE_FILE_LEAK = [
  'process_path',
  'process_name',
  'process_cmdline',
  'target_path',
  'registry_path',
  'registry_key_name',
  'registry_value_name',
]
const REGISTRY_LEAK = [
  'registry_path',
  'registry_key_name',
  'registry_value_name',
]

const elasticMap: DatasourceMap = {
  Elastic: {
    any: {
      process_creation: {},
      file_event: {},
      registry_event: { unsupportedFields: CORE_PROCESS_LEAK },
      dns: { unsupportedFields: [...CORE_PROCESS_LEAK, ...REGISTRY_LEAK] },
      network_connection: {
        unsupportedFields: [...CORE_PROCESS_LEAK, ...REGISTRY_LEAK],
      },
      image_load: { unsupportedFields: CORE_FILE_LEAK },
      system: { unsupportedFields: [...CORE_PROCESS_LEAK, ...REGISTRY_LEAK] },
      security: { unsupportedFields: [...CORE_PROCESS_LEAK, ...REGISTRY_LEAK] },
      authentication_events: {
        unsupportedFields: [...CORE_PROCESS_LEAK, ...REGISTRY_LEAK],
      },
      pipe_created: { unsupportedFields: CORE_PROCESS_LEAK },
    },
  },
}

export class ElasticTool extends Tool {
  public readonly toolName = 'Elastic'
  public readonly toolKind = 'siem' as const
  public override readonly customMapping = true
  public override readonly datasourceMap = elasticMap

  public override supportedLogCategories(): string[] {
    return [
      'endpoint:sysmon',
      'endpoint:wineventlog',
      'network',
      'web',
      'http',
      'dns',
      'cloud',
    ]
  }

  /**
   * Parse Elastic .ndjson export mapping
   * @param elasticExportContents .ndjson newline-delimited JSON file
   */
  public parseMapUpload(elasticExportContents: string): CustomerMapParsed {
    const parsedMap: CustomerMapParsed = []
    const jsonLines = elasticExportContents.split('\n')

    // check JSON for massive amount of values
    if (jsonLines.length > 5000) {
      throw new MatchboiError('JSON file too large, export invalid?')
    }

    for (const jsonLine of jsonLines) {
      const jsonParsed = JSON.parse(jsonLine)

      if (!('attributes' in jsonParsed)) {
        continue
      }

      const fields: Array<string> = []
      for (const fieldJsonParsed of JSON.parse(
        jsonParsed['attributes']['fields'],
      )) {
        fields.push(fieldJsonParsed['name'])
      }

      const customerData: CustomerDataParsed = {
        index: jsonParsed['attributes']['title'],
        fields: fields,
      }

      parsedMap.push(customerData)
    }

    return parsedMap
  }

  /**
   * Stubbed out to disable until sorting out what to do about the "index" keys in the query payload 
  generateSearchPermalink(rootURL: string, renderedContent: string): string {
    const encodedRenderedContent = encodeURIComponent(
      rison.encode(JSON.parse(renderedContent))
    );
    return `${rootURL}discover#/?_g=(filters:!(),refreshInterval:(pause:!t,value:0),time:(from:'now-3h',to:'now'))&_a=(columns:!(),filters:!(('$state':(store:appState),${encodedRenderedContent}),interval:auto,query:(language:kuery,query:''),sort:!(!('@timestamp',desc)))`;
  }
   */

  public pivotEscape(key: string, value: string): string {
    return luceneEscape(value)
  }

  /** Notes and Implementation
   * Grouping
   * - InlineGroups: no root parens; nested groups parenthesized.
   *
   * Field mapping / wrapping
   * - toolTuningFields["elastic_lucene"] (1:1 today), fields wrapped with Eta: {{!=<mapped>!}}.
   *
   * Equality / Inequality
   * - eq:  F:"v"                 | multi: (F:"v1" OR F:"v2")
   * - neq: NOT F:"v"             | multi: NOT (F:"v1" OR F:"v2")
   *   - Numbers are left **bare** (`F:123`), strings **double-quoted** with escaping.
   *
   * Contains / Starts / Ends (regex)
   * - Lucene regex matches the entire term, so:
   *   - contains      → F:/.*v.* /              (multi → F:/.*(v1|v2).* /)   //added space after * due to comment breaking
   *   - startswith    → F:/(v1|v2).* /          (single → F:/v.* /)          //added space after * due to comment breaking
   *   - endswith      → F:/.*(v1|v2)/          (single → F:/.*v/)
   * - Negation via `NOT F:/.../`.
   * - No inline flags (Lucene query-string regex has no `/i`). Case behavior is analyzer-dependent.
   *
   * Comparisons (range)
   * - lt  → F:{* TO v}
   * - lte → F:[* TO v]
   * - gt  → F:{v TO *}
   * - gte → F:[v TO *]
   *   (Numbers bare; strings quoted if needed.)
   *
   * Null / Empty
   * - isnull     → _missing_:F
   * - isnotnull  → _exists_:F
   * - isempty    → F:""           (best effort; often not indexed)
   * - isnotempty → NOT F:""
   *
   * Safety
   * - Field identifiers sanitized by mapField; values escaped/quoted centrally.
   */

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: InlineGroups,
    renderContext: {
      andGlue: ' AND ',
      orGlue: ' OR ',
      wrapField: (f) => etaFieldLookup(f), // safe Eta key-lookup (SSTI-hardened)
      // Standard double-quote with escapes
      quote: (s: string) =>
        `"${String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`,
      // list() not used directly; we build OR groups explicitly for eq/multi
    },
    formatPredicate: (
      p: AstPredicate,
      renderContext: RenderContext,
    ): string => {
      const F = renderContext.wrapField(renderContext.mapField(p.field))

      // Keep raw for type-aware quoting (numbers bare)
      const raw: ValueOrList | undefined = p.value
      const rawList =
        raw == null ? [undefined] : Array.isArray(raw) ? raw : [raw]

      // String list for shaping
      const vals = normalizeList(p.value).map(scalarToString)
      const isMulti = vals.length > 1

      // Smart quote: numbers bare; strings quoted
      const Q = (s: string, rv: any) =>
        typeof rv === 'number' ? String(rv) : renderContext.quote(s)

      // Build a Lucene regex body (no flags; matches entire term)
      const rxContains = (vals: string[]) =>
        vals.length === 1
          ? `.*${escapeRx(vals[0])}.*`
          : `.*(${vals.map(escapeRx).join('|')}).*`
      const rxStarts = (vals: string[]) =>
        vals.length === 1
          ? `${escapeRx(vals[0])}.*`
          : `(${vals.map(escapeRx).join('|')}).*`
      const rxEnds = (vals: string[]) =>
        vals.length === 1
          ? `.*${escapeRx(vals[0])}`
          : `.*(${vals.map(escapeRx).join('|')})`

      // Helpers
      const OR = (parts: string[]) => `(${parts.join(renderContext.orGlue)})`
      const NOT = (s: string) => `NOT ${s}`

      /* Null / Empty */
      switch (p.op) {
        case 'isnull':
          return `_missing_:${F}`
        case 'isnotnull':
          return `_exists_:${F}`
        case 'isempty':
          return `${F}:${renderContext.quote('')}`
        case 'isnotempty':
          return `NOT ${F}:${renderContext.quote('')}`
      }

      /*  Equality / Inequality  */
      if (p.op === 'eq' || p.op === 'neq') {
        const parts = vals.map((s, i) => `${F}:${Q(s, rawList[i])}`)
        if (!isMulti) return p.op === 'eq' ? parts[0] : NOT(parts[0])
        const group = OR(parts)
        return p.op === 'eq' ? group : NOT(group)
      }

      /*  Regex Handling for other operators (contains/starts/ends)  */
      if (
        p.op === 'contains' ||
        p.op === 'doesnotcontain' ||
        p.op === 'startswith' ||
        p.op === 'endswith'
      ) {
        const body =
          p.op === 'contains' || p.op === 'doesnotcontain'
            ? rxContains(vals)
            : p.op === 'startswith'
              ? rxStarts(vals)
              : rxEnds(vals)
        const clause = `${F}:/${body}/`
        return p.op === 'doesnotcontain' ? NOT(clause) : clause
      }

      /*  Comparisons via supported ranges  */
      if (p.op === 'lt' || p.op === 'lte' || p.op === 'gt' || p.op === 'gte') {
        const s0 = vals[0]
        const r0 = rawList[0]
        const v0 = Q(s0, r0)
        switch (p.op) {
          case 'lt':
            return `${F}:{* TO ${v0}}`
          case 'lte':
            return `${F}:[* TO ${v0}]`
          case 'gt':
            return `${F}:{${v0} TO *}`
          case 'gte':
            return `${F}:[${v0} TO *]`
        }
      }

      /* Fallback to Default */
      if (isMulti) {
        const parts = vals.map((s, i) => `${F}:${Q(s, rawList[i])}`)
        return OR(parts)
      }
      return `${F}:${Q(vals[0], rawList[0])}`
    },
  })

  public readonly tuningText =
    'Set the query language to Lucene (not KQL). Place the generated query in the search bar. Value matching is case-sensitive by default.'
}
